import { h, Component } from 'preact';

import Store from 'store/store';
import { getCopy } from 'utils/copy';
import { accessNested, joinClasses, joinPaths, sortObjectByOrder } from 'utils/utils';
import { getPagePath } from 'utils/bolt';
import getCopyWithParams from 'utils/getCopyWithParams';

import Button from 'components/bolt/button/button';
import Link from 'components/bolt/link/link';
import Close from 'components/bolt/icons/close';
import Facebook from 'components/bolt/icons/facebook';
import Whatsapp from 'components/bolt/icons/whatsapp';
import Twitter from 'components/bolt/icons/twitter';
import Pinterest from 'components/bolt/icons/pinterest';
import Tumblr from 'components/bolt/icons/tumblr';
import Viber from 'components/bolt/icons/viber';
import Mail from 'components/bolt/icons/mail';
import Telegram from 'components/bolt/icons/telegram';
import Copy from 'components/bolt/icons/copy';
import Check from 'components/bolt/icons/check';
import Instagram from '../icons/instagram';

import s from 'components/bolt/header/sharePopout.sss';


const icons = {
	facebook: <Facebook class={s.facebook} />,
	whatsapp: <Whatsapp class={s.whatsapp} />,
	twitter: <Twitter class={s.twitter} />,
	pinterest: <Pinterest class={s.pinterest} />,
	email: <Mail class={s.mail} />,
	tumblr: <Tumblr class={s.tumblr} />,
	viber: <Viber class={s.viber} />,
	telegram: <Telegram class={s.telegram} />,
	instagram: <Instagram class={s.instagram} />
};

export default class SharePopout extends Component {

	constructor(props) {
		super(props);

		this.state = {
			copied: false
		};

		const supportedTypes = ['email', 'facebook', 'pinterest', 'telegram', 'tumblr', 'twitter', 'viber', 'whatsapp', 'instagram'];

		const data = accessNested(props, 'appData.header.powsterHeader.share') || {};
		this.shareItems = sortObjectByOrder(data).filter(type => supportedTypes.includes(type.id));
		this.shareImage = data.shareImage;
		this.shareCopy = getCopyWithParams(props.appData?.copy?.baseCopy, data.socialCopy || '$SOCIAL_COPY');
		this.shareUrl = data.shareUrl && data.shareUrl.startsWith('$') ? getCopy(data.shareUrl, {}, '', null, 'baseCopy') : data.shareUrl;
		if (!this.shareUrl) {
			this.shareUrl = accessNested(this.props, 'appData.meta.url', null);
		}
		if (typeof window !== 'undefined' && __IS_LOCAL__) {
			let relativeUrl = location.origin + location.pathname;
			let currentPageData = props.appData?.pages?.[Store.get().activePage];
			if (currentPageData) {
				// Should get that from utils
				relativeUrl += getPagePath(currentPageData).path.slice(1).split('/').slice(1).fill('../').join('');
			}
			this.shareUrl = new URL(relativeUrl, location.href).href.replace(/\/$/, '');
		}

		this.getSharingLink = this.getSharingLink.bind(this);
		this.renderIcon = this.renderIcon.bind(this);
		this.copyToClipboard = this.copyToClipboard.bind(this);
		this.handleClose = this.handleClose.bind(this);
	}

	componentDidMount() {
		if (this.props.shareOpen) this.base.showModal();
	}

	componentDidUpdate(prevProps) {
		if (this.props.shareOpen && !prevProps.shareOpen) {
			this.base.showModal();
		} else if (!this.props.shareOpen && prevProps.shareOpen) {
			this.base.close();
		}
	}

	getShareURL() {
		if (this.props.shareUrlOverride) return this.props.shareUrlOverride;
		const useMetaUrl = accessNested(this.props, 'appData.header.powsterHeader.share.useMetaUrl', false);
		if (useMetaUrl) {
			return this.props?.appData?.meta?.url;
		}

		if (this.props.shareUrl) {
			return this.props.shareUrl;
		}
		return joinPaths(this.shareUrl, this.props.shareDetails);
	}

	removeLocationQueryParameter(str) {
		if (!str) return str;
		try {
			let url = new URL(str, location.href);
			url.searchParams.delete('location');
			url.searchParams.delete('lat');
			url.searchParams.delete('lon');
			return url.toString();
		} catch (e) {
			return str;
		}
	}

	getSharingLink(type) {
		let safeString = (str) => JSON.stringify((str || '') + '').slice(1, -1);
		const shareData = accessNested(this.props, 'appData.header.powsterHeader.share') || {};

		const shareCopy = this.props.shareCopyOverride || this.shareCopy;
		const shareTitle = this.props.shareTitleOverride || null;
		const shareImage = this.props.shareImage || this.shareImage;
		const combinedCopy = shareTitle ? `${shareTitle}\n${shareCopy}` : shareCopy;

		let url = this.getShareURL(type);
		url = this.removeLocationQueryParameter(url);
		url = encodeURIComponent(url);

		const URLS = {
			facebook: accessNested(shareData, 'facebook.shareUrl', `https://www.facebook.com/sharer/sharer.php?u=${url}`),
			twitter: accessNested(shareData, 'twitter.shareUrl', `https://twitter.com/intent/post?text=${combinedCopy}&url=${url}`),
			whatsapp: accessNested(shareData, 'whatsapp.shareUrl', `whatsapp://send?text=${combinedCopy} ${url}`),
			pinterest: accessNested(shareData, 'pinterest.shareUrl', `https://www.pinterest.co.uk/pin/create/button/?url=${url}&media=${shareImage}&description=${combinedCopy}`),
			email: accessNested(shareData, 'email.shareUrl', `mailto:?subject=${shareTitle}&body=${url}`),
			tumblr: accessNested(shareData, 'tumblr.shareUrl', `http://www.tumblr.com/share/link?url=${url}/&description=${shareCopy}`),
			viber: accessNested(shareData, 'viber.shareUrl', `viber://forward?text=${url} ${combinedCopy}`),
			telegram: accessNested(shareData, 'telegram.shareUrl', `https://telegram.me/share/url?url=${url}&text=${combinedCopy}`),
			instagram: accessNested(shareData, 'instagram.shareUrl', `https://www.instagram.com/`) // TODO: Find better way of doing this 
		};
		// if %SOCIAL_COPY% has been used and replaced, but %SOCIAL_COPY% itself contains %MOVIE_NAME%
		// moved %SOCIAL_COPY and %SHARE_URL% from expandAppData to here to fix encodeURIComponent issue on Thundr builds

		if (type === 'email' && this.props.shareCopyOverride ) {
			return URLS[type]
				.replace(/%SOCIAL_COPY%/g, shareTitle || shareCopy)
				.replace(/%SHARE_URL%/g, `${shareCopy}\n\n ${url}`)
				.replace(/%MOVIE_NAME%/g, safeString(this.props.appData.meta.title.locale || this.props.appData.meta.title.en))
				.replace(/%HEADER_SHARE_IMAGE%/g, safeString(shareImage))
				.replace(/%SHARE_IMAGE%/g, safeString(shareImage));
		}

		return URLS[type]
			.replace(/%MOVIE_NAME%/g, safeString(this.props.appData.meta.title.locale || this.props.appData.meta.title.en))
			.replace(/%SOCIAL_COPY%/g, encodeURIComponent(combinedCopy))
			.replace(/%SHARE_URL%/g, url)
			.replace(/%HEADER_SHARE_IMAGE%/g, safeString(shareImage))
			.replace(/%SHARE_IMAGE%/g, safeString(shareImage));
	}

	copyToClipboard() {
		let url = this.getShareURL('copy');
		navigator.clipboard.writeText(url);

		this.setState({ copied: true });

		if (this.props.shareUrlOverride) {
			Store.emit('INVITE_GENERATOR_SHARE_INVITE_CLICK', { label: 'copy', activePage: Store.get().activePage });
		} else {
			Store.emit('CLICKED_SHARE', { label: 'copy', activePage: Store.get().activePage });
		}

		this.copyTimeout = window.setTimeout(() => {
			this.setState({ copied: false });
		}, 1000);
	}

	renderIcon(type) {
		return icons[type];
	}

	handleClose() {
		this.setState({ copied: false });
		this.props.toggleSharePop();
	}

	render(props, { copied }) {
		const { shareOpen, closeBtnstyle, extraClass } = props;
		const store = Store.get();
		const shareCTA = accessNested(this.props, 'appData.header.powsterHeader.share.shareCTA');
		const fontStyles = accessNested(this.props, 'appData.styling.fontVariables');
		const accessibilityCopy = accessNested(this.props, 'appData.copy.accessibilityCopy', {});
		const showtimesInvitesEnabled = accessNested(store, 'appData.pages.boltShowtimes.data.interstitialInviteGenerator.enabled', false);
		const activePage = store.activePage;
		const externalBannerOpen = accessNested(store, 'externalCookieBanner.bannerOpen', false);
		let cookieHeight = (externalBannerOpen ? accessNested(store, 'externalCookieBanner.height') : accessNested(store, 'cookies.height')) || 0;
		const sonyHeaderEnabled = accessNested(store, 'appData.options.sonyFeatures.sonyHeader', false);
		if (sonyHeaderEnabled) {
			cookieHeight += 34;
		}

		return (
			<dialog class={joinClasses(s.wrapper, shareOpen && s.open, extraClass && extraClass)} style={{ marginTop: cookieHeight + 'px' }} onCancel={this.handleClose}>
				<div class={s.container} style={{ marginTop: cookieHeight + 'px' }}>
					<div class={s.headline}>
						<p class={fontStyles.headerShareCta}>{getCopy(shareCTA, {}, '', 'baseCopy') || shareCTA || 'Create social post'}</p>
						<Button class={s.close} trigger={this.handleClose} style={closeBtnstyle} ariaLabel={accessibilityCopy.$CLOSE_SHARE_ARIA}>
							<Close aria-label={accessibilityCopy.$CLOSE_SHARE_ARIA} />
						</Button>
					</div>
					<div class={s.shareButtons}>
						<Button class={joinClasses(s.copyButton, copied && s.copied)} trigger={this.copyToClipboard} ariaLabel={accessibilityCopy.$COPY_LINK_BTN_ARIA}>
							{copied ? <Check class={s.copy} /> : <Copy class={s.copy} />}
						</Button>
						{this.shareItems.map((item) => {
							const type = item.id.toLowerCase();
							return (
								<Link
									class={joinClasses(s.shareButton, s[`shareButton--${type}`])}
									href={this.getSharingLink(type)}
									target="_blank"
									rel="noopener noreferrer"
									onClick={() => {
										if (this.props.shareUrlOverride) {
											Store.emit('INVITE_GENERATOR_SHARE_INVITE_CLICK', { label: type, activePage });
										} else {
											Store.emit('CLICKED_SHARE', { label: type, activePage });
										}
									}}
									ariaLabel={accessibilityCopy.$SHARE_ARIA.replace('<platform>', item.title)}
								>
									{this.renderIcon(type)}
								</Link>
							);
						})}
					</div>
				</div>
				<div class={s.backgroundContainer} onClick={this.handleClose} />
			</dialog>
		);
	}
}
